import {
  formatCurrency,
  formatDate,
  formatPercent,
  StatusBadge,
  type Severity
} from '@ijoin/components';
import { useGetEnrollmentDetail } from '@ijoin/hooks';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Stack,
  Typography
} from '@mui/material';

import { FC } from 'react';

import { Field } from './Field';

const DATE_ONLY = { format: 'MM/DD/YYYY', useUtc: true } as const;

const ENROLLMENT_ORIGIN_DESCRIPTIONS = [
  {
    label: 'Record Keeper Sync',
    meaning:
      'Created or last updated by a plan/participant import from the record keeper.'
  },
  {
    label: 'iJoin Finalized Enrollment',
    meaning:
      'The participant completed and submitted an enrollment through the iJoin experience.'
  },
  {
    label: 'iJoin In Progress Enrollment',
    meaning:
      'The participant started an iJoin enrollment but has not finalized it yet.'
  },
  {
    label: 'iJoin Post Finalized Pre-Enrollment',
    meaning:
      'The participant re-entered the experience after finalizing; new elections are staged but not yet finalized.'
  },
  {
    label: 'iJoin Manual Import',
    meaning: 'Created by a manually imported participant file.'
  },
  {
    label: 'iJoin Plan Revision',
    meaning:
      'Created when the enrollment was carried over to a new revision of the plan.'
  },
  {
    label: 'Managed Account Scheduled Analysis',
    meaning:
      'Updated by a scheduled Managed Account Program analysis of the participant.'
  },
  {
    label: 'Managed Account Automatic Reallocation',
    meaning:
      'Allocations were updated automatically by a Managed Account Program reallocation.'
  },
  {
    label: 'Unknown',
    meaning: 'The origin was not recorded (typically legacy data).'
  }
];

const INFO_STATUS_SEVERITY: Record<string, Severity> = {
  Active: 'success',
  Inactive: 'error',
  Ineligible: 'error',
  Terminated: 'error'
};

export const EnrollmentInfoSection: FC<{
  planEnrollmentId: string;
  planId: string;
}> = props => {
  const detail = useGetEnrollmentDetail(props.planId, props.planEnrollmentId);

  if (detail.isLoading) return <CircularProgress />;
  if (!detail.data) return <Typography>No data found.</Typography>;

  return (
    <Card sx={{ p: 1 }}>
      <CardHeader title='Enrollment Details' />
      <CardContent>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: 'repeat(3, 1fr)'
          }}>
          <div>
            <Typography gutterBottom variant='overline'>
              Personal
            </Typography>
            <Stack spacing={1}>
              <Field
                label='Full Name'
                value={`${detail.data.firstName} ${detail.data.lastName}`}
              />
              <Field
                label='Date of Birth'
                value={
                  detail.data.dateOfBirth
                    ? formatDate(detail.data.dateOfBirth, DATE_ONLY)
                    : '-'
                }
              />
              <Field
                label='SSN'
                value={detail.data.socialSecurityNumber ?? '-'}
              />
              {!!detail.data.gender && (
                <Field label='Gender' value={detail.data.gender} />
              )}
              <Field label='Personal Email' value={detail.data.email ?? '-'} />
              <Field label='Work Email' value={detail.data.workEmail ?? '-'} />
              <Field label='Cell Phone' value={detail.data.cellPhone ?? '-'} />
            </Stack>
          </div>

          <div>
            <Typography gutterBottom variant='overline'>
              Employment
            </Typography>
            <Stack spacing={1}>
              <Field
                label='Employment Status'
                value={
                  <StatusBadge
                    severity={
                      INFO_STATUS_SEVERITY[detail.data.employmentStatus] ??
                      'warning'
                    }>
                    {detail.data.employmentStatus}
                  </StatusBadge>
                }
              />
              <Field
                label='Participation Status'
                value={
                  <StatusBadge
                    severity={
                      INFO_STATUS_SEVERITY[detail.data.participationStatus] ??
                      'warning'
                    }>
                    {detail.data.participationStatus}
                  </StatusBadge>
                }
              />
              <Field
                label='Hire Date'
                value={
                  detail.data.dateOfHire
                    ? formatDate(detail.data.dateOfHire, DATE_ONLY)
                    : '-'
                }
              />
              <Field
                label='Rehire Date'
                value={
                  detail.data.rehireDate
                    ? formatDate(detail.data.rehireDate, DATE_ONLY)
                    : '-'
                }
              />
              <Field
                label='Termination Date'
                value={
                  detail.data.terminationDate
                    ? formatDate(detail.data.terminationDate, DATE_ONLY)
                    : '-'
                }
              />
              {detail.data.eligibilityDate && (
                <Field
                  label='Eligibility Date'
                  value={formatDate(detail.data.eligibilityDate, DATE_ONLY)}
                />
              )}
              {detail.data.projectedEligibilityDate && (
                <Field
                  label='Projected Eligibility'
                  value={formatDate(
                    detail.data.projectedEligibilityDate,
                    DATE_ONLY
                  )}
                />
              )}
              {!!detail.data.payPeriodsPerYear && (
                <Field
                  label='Pay Periods / Year'
                  value={detail.data.payPeriodsPerYear}
                />
              )}
            </Stack>
          </div>

          <div>
            <Typography gutterBottom variant='overline'>
              Financial & Enrollment
            </Typography>
            <Stack spacing={1}>
              <Field
                label='Annual Salary'
                value={formatCurrency(detail.data.annualSalary ?? 0)}
              />
              <Field
                label='Stock Balance'
                value={formatCurrency(detail.data.totalStockBalance ?? 0)}
              />
              <Field
                label='Bond Balance'
                value={formatCurrency(detail.data.totalBondBalance ?? 0)}
              />
              <Field
                infoTooltip={
                  <Stack spacing={0.5} sx={{ p: 0.5 }}>
                    {ENROLLMENT_ORIGIN_DESCRIPTIONS.map(origin => (
                      <Typography key={origin.label} variant='caption'>
                        <strong>{origin.label}:</strong> {origin.meaning}
                      </Typography>
                    ))}
                  </Stack>
                }
                label='Enrollment Origin'
                value={detail.data.enrollmentOrigin ?? ''}
              />
              <Field
                label='Last Sync Date'
                value={
                  detail.data.syncDate ? formatDate(detail.data.syncDate) : '-'
                }
              />
              <Field
                label='Pre-Tax Auto Increase'
                value={
                  <StatusBadge
                    severity={
                      detail.data.hasPreTaxAutoIncrease ? 'success' : 'warning'
                    }>
                    {detail.data.hasPreTaxAutoIncrease ? 'ACTIVE' : 'INACTIVE'}
                  </StatusBadge>
                }
              />
              <Field
                label='Roth Auto Increase'
                value={
                  <StatusBadge
                    severity={
                      detail.data.hasRothAutoIncrease ? 'success' : 'warning'
                    }>
                    {detail.data.hasRothAutoIncrease ? 'ACTIVE' : 'INACTIVE'}
                  </StatusBadge>
                }
              />
              {detail.data.hasPreTaxAutoIncrease &&
                detail.data.preTaxAutoIncreaseEffectiveDate && (
                  <Field
                    label='Pre-Tax Auto Increase Effective Date'
                    value={formatDate(
                      detail.data.preTaxAutoIncreaseEffectiveDate,
                      DATE_ONLY
                    )}
                  />
                )}
              {detail.data.hasRothAutoIncrease &&
                detail.data.rothAutoIncreaseEffectiveDate && (
                  <Field
                    label='Roth Auto Increase Effective Date'
                    value={formatDate(
                      detail.data.rothAutoIncreaseEffectiveDate,
                      DATE_ONLY
                    )}
                  />
                )}
            </Stack>
          </div>
        </Box>

        {detail.data.investmentVehicleEnrollments?.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography gutterBottom variant='overline'>
              Investment Vehicle Enrollments
            </Typography>

            {detail.data.investmentVehicleEnrollments.map(ive => (
              <Box key={ive.investmentVehicleEnrollmentId} sx={{ mt: 2 }}>
                <Typography gutterBottom variant='subtitle2'>
                  {ive.investmentVehicleTypeDescription}
                  {ive.contractNumber ? ` (${ive.contractNumber})` : ''}
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: 'repeat(3, 1fr)'
                  }}>
                  <Stack spacing={1}>
                    <Field
                      label='Pre-Tax Balance'
                      value={formatCurrency(
                        ive.currentPreTaxAccountBalance ?? 0
                      )}
                    />
                    <Field
                      label='Roth Balance'
                      value={formatCurrency(
                        ive.currentPostTaxAccountBalance ?? 0
                      )}
                    />
                    {ive.externalTotalPlanBalance > 0 && (
                      <Field
                        label='Total Balance'
                        value={formatCurrency(ive.externalTotalPlanBalance)}
                      />
                    )}
                    {ive.nonRothPostTaxAccountBalance > 0 && (
                      <Field
                        label='Non-Roth Post-Tax'
                        value={formatCurrency(ive.nonRothPostTaxAccountBalance)}
                      />
                    )}
                    {ive.rolloverAccountBalance > 0 && (
                      <Field
                        label='Rollover Balance'
                        value={formatCurrency(ive.rolloverAccountBalance)}
                      />
                    )}
                    {ive.employerMatchAccountBalance > 0 && (
                      <Field
                        label='Employer Match Balance'
                        value={formatCurrency(ive.employerMatchAccountBalance)}
                      />
                    )}
                    {ive.employerOtherAccountBalance > 0 && (
                      <Field
                        label='Employer Other Balance'
                        value={formatCurrency(ive.employerOtherAccountBalance)}
                      />
                    )}
                    {ive.totalRothContributionBasis > 0 && (
                      <Field
                        label='Roth Contribution Basis'
                        value={formatCurrency(ive.totalRothContributionBasis)}
                      />
                    )}
                    {ive.definedMonthlyBenefit > 0 && (
                      <Field
                        label='Monthly Benefit'
                        value={formatCurrency(ive.definedMonthlyBenefit)}
                      />
                    )}
                    {ive.monthlyBenefitAge > 0 && (
                      <Field
                        label='Monthly Benefit Age'
                        value={ive.monthlyBenefitAge}
                      />
                    )}
                    {ive.vestedPreTaxBalance > 0 && (
                      <Field
                        label='Vested Pre-Tax'
                        value={formatCurrency(ive.vestedPreTaxBalance)}
                      />
                    )}
                    {ive.vestedPostTaxBalance > 0 && (
                      <Field
                        label='Vested Roth'
                        value={formatCurrency(ive.vestedPostTaxBalance)}
                      />
                    )}
                    {ive.vestedNonRothPostTaxBalance > 0 && (
                      <Field
                        label='Vested Non-Roth Post-Tax'
                        value={formatCurrency(ive.vestedNonRothPostTaxBalance)}
                      />
                    )}
                    {ive.vestedRolloverBalance > 0 && (
                      <Field
                        label='Vested Rollover'
                        value={formatCurrency(ive.vestedRolloverBalance)}
                      />
                    )}
                    {ive.vestedEmployerMatchBalance > 0 && (
                      <Field
                        label='Vested Employer Match'
                        value={formatCurrency(ive.vestedEmployerMatchBalance)}
                      />
                    )}
                    {ive.vestedEmployerOtherBalance > 0 && (
                      <Field
                        label='Vested Employer Other'
                        value={formatCurrency(ive.vestedEmployerOtherBalance)}
                      />
                    )}
                    {ive.outsidePreTaxBalance > 0 && (
                      <Field
                        label='Outside Pre-Tax'
                        value={formatCurrency(ive.outsidePreTaxBalance)}
                      />
                    )}
                    {ive.outsideRothBalance > 0 && (
                      <Field
                        label='Outside Roth'
                        value={formatCurrency(ive.outsideRothBalance)}
                      />
                    )}
                    {ive.outsidePostTaxBalance > 0 && (
                      <Field
                        label='Outside Post-Tax'
                        value={formatCurrency(ive.outsidePostTaxBalance)}
                      />
                    )}
                    {ive.outsideMonthlyPension > 0 && (
                      <Field
                        label='Outside Pension'
                        value={formatCurrency(ive.outsideMonthlyPension)}
                      />
                    )}
                    {ive.outsideSpousalPreTaxBalance > 0 && (
                      <Field
                        label='Spousal Pre-Tax'
                        value={formatCurrency(ive.outsideSpousalPreTaxBalance)}
                      />
                    )}
                    {ive.outsideSpousalRothBalance > 0 && (
                      <Field
                        label='Spousal Roth'
                        value={formatCurrency(ive.outsideSpousalRothBalance)}
                      />
                    )}
                    {ive.outsideSpousalPostTaxBalance > 0 && (
                      <Field
                        label='Spousal Post-Tax'
                        value={formatCurrency(ive.outsideSpousalPostTaxBalance)}
                      />
                    )}
                    {ive.outsideSpousalMonthlyPension > 0 && (
                      <Field
                        label='Spousal Pension'
                        value={formatCurrency(ive.outsideSpousalMonthlyPension)}
                      />
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    <Field
                      label='Current Pre-Tax %'
                      value={
                        ive.currentPreTaxContributionPercent > 0
                          ? formatPercent(
                              ive.currentPreTaxContributionPercent,
                              { fractionDigits: 2 }
                            )
                          : '-'
                      }
                    />
                    <Field
                      label='Current Roth %'
                      value={
                        ive.currentPostTaxContributionPercent > 0
                          ? formatPercent(
                              ive.currentPostTaxContributionPercent,
                              { fractionDigits: 2 }
                            )
                          : '-'
                      }
                    />
                    {ive.currentPreTaxContributionDollars > 0 && (
                      <Field
                        label='Current Pre-Tax $'
                        value={formatCurrency(
                          ive.currentPreTaxContributionDollars
                        )}
                      />
                    )}
                    {ive.currentPostTaxContributionDollars > 0 && (
                      <Field
                        label='Current Roth $'
                        value={formatCurrency(
                          ive.currentPostTaxContributionDollars
                        )}
                      />
                    )}
                    {ive.newPreTaxContributionPercent > 0 && (
                      <Field
                        label='New Pre-Tax %'
                        value={formatPercent(ive.newPreTaxContributionPercent, {
                          fractionDigits: 2
                        })}
                      />
                    )}
                    {ive.newPreTaxContributionDollars > 0 && (
                      <Field
                        label='New Pre-Tax $'
                        value={formatCurrency(ive.newPreTaxContributionDollars)}
                      />
                    )}
                    {ive.newPostTaxContributionPercent > 0 && (
                      <Field
                        label='New Roth %'
                        value={formatPercent(
                          ive.newPostTaxContributionPercent,
                          { fractionDigits: 2 }
                        )}
                      />
                    )}
                    {ive.newPostTaxContributionDollars > 0 && (
                      <Field
                        label='New Roth $'
                        value={formatCurrency(
                          ive.newPostTaxContributionDollars
                        )}
                      />
                    )}
                    {ive.preTaxDeferralYtdDollars > 0 && (
                      <Field
                        label='Pre-Tax Deferral YTD'
                        value={formatCurrency(ive.preTaxDeferralYtdDollars)}
                      />
                    )}
                    {ive.preTaxDeferralYtdCatchupDollars > 0 && (
                      <Field
                        label='Pre-Tax Catchup YTD'
                        value={formatCurrency(
                          ive.preTaxDeferralYtdCatchupDollars
                        )}
                      />
                    )}
                    {ive.rothDeferralYtdDollars > 0 && (
                      <Field
                        label='Roth Deferral YTD'
                        value={formatCurrency(ive.rothDeferralYtdDollars)}
                      />
                    )}
                    {ive.rothDeferralYtdCatchupDollars > 0 && (
                      <Field
                        label='Roth Catchup YTD'
                        value={formatCurrency(
                          ive.rothDeferralYtdCatchupDollars
                        )}
                      />
                    )}
                    {ive.postTaxDeferralYtdDollars > 0 && (
                      <Field
                        label='Post-Tax Deferral YTD'
                        value={formatCurrency(ive.postTaxDeferralYtdDollars)}
                      />
                    )}
                    {ive.nqdcDeferralYtdDollars > 0 && (
                      <Field
                        label='NQDC Deferral YTD'
                        value={formatCurrency(ive.nqdcDeferralYtdDollars)}
                      />
                    )}
                    {ive.employerMatchingContributionYtdDollars > 0 && (
                      <Field
                        label='Employer Match YTD'
                        value={formatCurrency(
                          ive.employerMatchingContributionYtdDollars
                        )}
                      />
                    )}
                    {ive.employerOtherContributionYtdDollars > 0 && (
                      <Field
                        label='Employer Other YTD'
                        value={formatCurrency(
                          ive.employerOtherContributionYtdDollars
                        )}
                      />
                    )}
                    {ive.preTaxDeferralCatchupPercent > 0 && (
                      <Field
                        label='Pre-Tax Catchup %'
                        value={formatPercent(ive.preTaxDeferralCatchupPercent, {
                          fractionDigits: 2
                        })}
                      />
                    )}
                    {ive.rothDeferralCatchupPercent > 0 && (
                      <Field
                        label='Roth Catchup %'
                        value={formatPercent(ive.rothDeferralCatchupPercent, {
                          fractionDigits: 2
                        })}
                      />
                    )}
                    {ive.postTaxDeferralPercent > 0 && (
                      <Field
                        label='Post-Tax Deferral %'
                        value={formatPercent(ive.postTaxDeferralPercent, {
                          fractionDigits: 2
                        })}
                      />
                    )}
                    {ive.nqdcDeferralPercent > 0 && (
                      <Field
                        label='NQDC Deferral %'
                        value={formatPercent(ive.nqdcDeferralPercent, {
                          fractionDigits: 2
                        })}
                      />
                    )}
                    {ive.employerOtherContributionPercent > 0 && (
                      <Field
                        label='Employer Other Contribution %'
                        value={formatPercent(
                          ive.employerOtherContributionPercent,
                          { fractionDigits: 2 }
                        )}
                      />
                    )}
                    {ive.externalPreTaxDollarContrAmt > 0 && (
                      <Field
                        label='External Pre-Tax Contribution'
                        value={formatCurrency(ive.externalPreTaxDollarContrAmt)}
                      />
                    )}
                    {ive.externalRothDollarContrAmt > 0 && (
                      <Field
                        label='External Roth Contribution'
                        value={formatCurrency(ive.externalRothDollarContrAmt)}
                      />
                    )}
                    {!!ive.initialRothContributionTaxYear && (
                      <Field
                        label='Initial Roth Tax Year'
                        value={ive.initialRothContributionTaxYear}
                      />
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    {detail.data.hasPreTaxAutoIncrease && (
                      <>
                        {ive.autoIncreasePreTaxAnnualPercent > 0 && (
                          <Field
                            label='Auto Increase Pre-Tax Annual %'
                            value={formatPercent(
                              ive.autoIncreasePreTaxAnnualPercent,
                              { fractionDigits: 2 }
                            )}
                          />
                        )}
                        {ive.autoIncreasePreTaxMaximumPercent > 0 && (
                          <Field
                            label='Auto Increase Pre-Tax Max %'
                            value={formatPercent(
                              ive.autoIncreasePreTaxMaximumPercent,
                              { fractionDigits: 2 }
                            )}
                          />
                        )}
                        {!!ive.preTaxAutoIncreaseFrequency && (
                          <Field
                            label='Pre-Tax Auto Increase Frequency'
                            value={ive.preTaxAutoIncreaseFrequency}
                          />
                        )}
                        {!!ive.autoIncreasePreTaxDate1 && (
                          <Field
                            label='Auto Increase Pre-Tax Date 1'
                            value={`${ive.autoIncreasePreTaxDate1.slice(0, 2)}/${ive.autoIncreasePreTaxDate1.slice(2)}`}
                          />
                        )}
                        {!!ive.autoIncreasePreTaxDate2 && (
                          <Field
                            label='Auto Increase Pre-Tax Date 2'
                            value={`${ive.autoIncreasePreTaxDate2.slice(0, 2)}/${ive.autoIncreasePreTaxDate2.slice(2)}`}
                          />
                        )}
                        {ive.preTaxAutoIncreaseAcceleration1Date && (
                          <Field
                            label='Pre-Tax Auto Increase Accel Date'
                            value={formatDate(
                              ive.preTaxAutoIncreaseAcceleration1Date,
                              DATE_ONLY
                            )}
                          />
                        )}
                        <Field
                          label='Auto Increase Pre-Tax Participant Directed'
                          value={
                            <StatusBadge
                              severity={
                                ive.autoIncreasePreTaxIsParticipantDirected
                                  ? 'success'
                                  : 'warning'
                              }>
                              {ive.autoIncreasePreTaxIsParticipantDirected
                                ? 'ACTIVE'
                                : 'INACTIVE'}
                            </StatusBadge>
                          }
                        />
                      </>
                    )}
                    {detail.data.hasRothAutoIncrease && (
                      <>
                        {ive.autoIncreaseRothAnnualPercent > 0 && (
                          <Field
                            label='Auto Increase Roth Annual %'
                            value={formatPercent(
                              ive.autoIncreaseRothAnnualPercent,
                              { fractionDigits: 2 }
                            )}
                          />
                        )}
                        {ive.autoIncreaseRothMaximumPercent > 0 && (
                          <Field
                            label='Auto Increase Roth Max %'
                            value={formatPercent(
                              ive.autoIncreaseRothMaximumPercent,
                              { fractionDigits: 2 }
                            )}
                          />
                        )}
                        {ive.rothAutoIncreasePercent > 0 && (
                          <Field
                            label='Roth Auto Increase %'
                            value={formatPercent(ive.rothAutoIncreasePercent, {
                              fractionDigits: 2
                            })}
                          />
                        )}
                        {!!ive.rothAutoIncreaseFrequency && (
                          <Field
                            label='Roth Auto Increase Frequency'
                            value={ive.rothAutoIncreaseFrequency}
                          />
                        )}
                        {!!ive.autoIncreaseRothDate1 && (
                          <Field
                            label='Auto Increase Roth Date 1'
                            value={`${ive.autoIncreaseRothDate1.slice(0, 2)}/${ive.autoIncreaseRothDate1.slice(2)}`}
                          />
                        )}
                        {!!ive.autoIncreaseRothDate2 && (
                          <Field
                            label='Auto Increase Roth Date 2'
                            value={`${ive.autoIncreaseRothDate2.slice(0, 2)}/${ive.autoIncreaseRothDate2.slice(2)}`}
                          />
                        )}
                        {ive.rothAutoIncreaseAcceleration1Date && (
                          <Field
                            label='Roth Auto Increase Accel Date'
                            value={formatDate(
                              ive.rothAutoIncreaseAcceleration1Date,
                              DATE_ONLY
                            )}
                          />
                        )}
                        <Field
                          label='Auto Increase Roth Participant Directed'
                          value={
                            <StatusBadge
                              severity={
                                ive.autoIncreaseRothIsParticipantDirected
                                  ? 'success'
                                  : 'warning'
                              }>
                              {ive.autoIncreaseRothIsParticipantDirected
                                ? 'ACTIVE'
                                : 'INACTIVE'}
                            </StatusBadge>
                          }
                        />
                      </>
                    )}
                    {(detail.data.hasPreTaxAutoIncrease ||
                      detail.data.hasRothAutoIncrease) && (
                      <>
                        {ive.autoIncreasePercent > 0 && (
                          <Field
                            label='Auto Increase %'
                            value={formatPercent(ive.autoIncreasePercent, {
                              fractionDigits: 2
                            })}
                          />
                        )}
                        {ive.autoIncreaseMaximumPercent > 0 && (
                          <Field
                            label='Auto Increase Max %'
                            value={formatPercent(
                              ive.autoIncreaseMaximumPercent,
                              { fractionDigits: 2 }
                            )}
                          />
                        )}
                      </>
                    )}
                    <Field
                      label='Auto Enroll Pre-Tax Directed'
                      value={
                        <StatusBadge
                          severity={
                            ive.autoEnrollPreTaxIsParticipantDirected
                              ? 'success'
                              : 'warning'
                          }>
                          {ive.autoEnrollPreTaxIsParticipantDirected
                            ? 'ACTIVE'
                            : 'INACTIVE'}
                        </StatusBadge>
                      }
                    />
                    <Field
                      label='Auto Enroll Roth Directed'
                      value={
                        <StatusBadge
                          severity={
                            ive.autoEnrollRothIsParticipantDirected
                              ? 'success'
                              : 'warning'
                          }>
                          {ive.autoEnrollRothIsParticipantDirected
                            ? 'ACTIVE'
                            : 'INACTIVE'}
                        </StatusBadge>
                      }
                    />
                  </Stack>
                </Box>
              </Box>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};

EnrollmentInfoSection.displayName = 'EnrollmentInfoSection';
